import { useState, useEffect, useRef } from 'react';
import { useBroker } from '../contexts/BrokerContext';
import { useLocale } from '../contexts/LocaleContext';
import { Search, Plus, Settings, Check, X, Loader, ChevronDown, TrendingUp, TrendingDown, Activity } from 'lucide-react';
import toast from 'react-hot-toast';
import SymbolSettingsModal from '../components/forms/SymbolSettingsModal';

// ─── Mobile detection ─────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

const CATEGORIES = ['all', 'forex', 'crypto', 'stock', 'index', 'commodity'];

function getCategoryColor(category) {
  return {
    forex:       'var(--accent-primary)',
    crypto:      'var(--accent-warning)',
    stocks:      'var(--accent-success)',
    indices:     'var(--accent-info)',
    commodities: 'var(--accent-danger)',
  }[category] || 'var(--text-tertiary)';
}

// ─── Price skeleton pulse ─────────────────────────────────────────────────────
function PriceSkeleton({ compact = false }) {
  if (compact) {
    return (
      <div className="flex items-center gap-1.5 animate-pulse">
        <div className="h-2.5 w-12 rounded" style={{ backgroundColor: 'var(--bg-tertiary)' }} />
        <div className="h-2.5 w-2 rounded" style={{ backgroundColor: 'var(--bg-tertiary)' }} />
        <div className="h-2.5 w-12 rounded" style={{ backgroundColor: 'var(--bg-tertiary)' }} />
      </div>
    );
  }
  return (
    <div className="mb-3 p-3 rounded-lg animate-pulse" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
      <div className="flex items-center justify-between mb-2">
        <div className="h-3 w-8 rounded" style={{ backgroundColor: 'var(--border-color)' }} />
        <div className="h-4 w-20 rounded" style={{ backgroundColor: 'var(--border-color)' }} />
      </div>
      <div className="flex items-center justify-between mb-2">
        <div className="h-3 w-8 rounded" style={{ backgroundColor: 'var(--border-color)' }} />
        <div className="h-4 w-20 rounded" style={{ backgroundColor: 'var(--border-color)' }} />
      </div>
      <div className="mt-2 pt-2 border-t flex items-center justify-between" style={{ borderColor: 'var(--border-color)' }}>
        <div className="h-3 w-12 rounded" style={{ backgroundColor: 'var(--border-color)' }} />
        <div className="h-3 w-16 rounded" style={{ backgroundColor: 'var(--border-color)' }} />
      </div>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function Instruments() {
  const { t } = useLocale();
  const isMobile = useIsMobile();
  const { 
    fetchFeedInstruments, 
    fetchEnabledInstruments, 
    enableInstrument, 
    disableInstrument, 
    bulkEnableInstruments, 
    updateSymbolSettings, 
    fetchSymbolSettings,
    brokerWsService,
    wsConnected
  } = useBroker();

  const [activeTab, setActiveTab]               = useState('library');
  const [feedInstruments, setFeedInstruments]   = useState([]);
  const [enabledInstruments, setEnabledInstruments] = useState([]);
  const [loading, setLoading]                   = useState(true);
  const [searchQuery, setSearchQuery]           = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [selectedInstruments, setSelectedInstruments] = useState([]);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedSymbol, setSelectedSymbol]     = useState(null);

  // Real-time prices: Map<symbol, {bid, ask, timestamp}>
  const [prices, setPrices] = useState(new Map());
  // Track symbols waiting for their first price tick — shows skeleton until received
  const [pendingPrices, setPendingPrices] = useState(new Set());
  // Keep a ref so the ws handler always sees the latest set
  const pendingRef = useRef(new Set());

  useEffect(() => { loadData(); }, [activeTab, selectedCategory]);

  // ── Subscribe to prices whenever instruments list or connection changes ──────
  useEffect(() => {
    if (!brokerWsService || !wsConnected) return;

    const data = activeTab === 'library' ? feedInstruments : enabledInstruments;
    if (!data.length) return;

    const symbols = data.map(i => i.symbol);

    // Mark every symbol as pending so skeletons appear immediately
    const pending = new Set(symbols);
    pendingRef.current = pending;
    setPendingPrices(new Set(pending));

    brokerWsService.subscribePrices(symbols);

    const unsubscribe = brokerWsService.on('price:update', (priceData) => {
      setPrices(prev => {
        const updated = new Map(prev);
        updated.set(priceData.symbol, {
          bid: priceData.bid,
          ask: priceData.ask,
          timestamp: priceData.timestamp || Date.now(),
        });
        return updated;
      });

      // Remove from pending once the first tick arrives
      if (pendingRef.current.has(priceData.symbol)) {
        pendingRef.current = new Set(pendingRef.current);
        pendingRef.current.delete(priceData.symbol);
        setPendingPrices(new Set(pendingRef.current));
      }
    });

    return () => {
      brokerWsService.unsubscribePrices(symbols);
      unsubscribe();
    };
  }, [brokerWsService, wsConnected, feedInstruments, enabledInstruments, activeTab]);

  // Reset prices & pending when switching tabs so stale data doesn't flash
  useEffect(() => {
    setPrices(new Map());
    setPendingPrices(new Set());
    pendingRef.current = new Set();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'library') {
        const r = await fetchFeedInstruments({ category: selectedCategory !== 'all' ? selectedCategory : undefined });
        setFeedInstruments(r.data.instruments || []);
      } else {
        const r = await fetchEnabledInstruments({ category: selectedCategory !== 'all' ? selectedCategory : undefined });
        setEnabledInstruments(r.data.instruments || []);
      }
    } catch { toast.error('Failed to load instruments'); }
    finally { setLoading(false); }
  };

  const handleEnableInstrument  = async (symbol) => { try { await enableInstrument(symbol);  toast.success(`${symbol} enabled`);  loadData(); } catch (e) { toast.error(e.message || 'Failed'); } };
  const handleDisableInstrument = async (symbol) => { try { await disableInstrument(symbol); toast.success(`${symbol} removed`);  loadData(); } catch (e) { toast.error(e.message || 'Failed'); } };

  const handleBulkEnable = async () => {
    if (!selectedInstruments.length) { toast.error('Please select instruments first'); return; }
    try {
      const result = await bulkEnableInstruments(selectedInstruments);
      toast.success(`Enabled ${result.data.success.length} instruments`);
      setSelectedInstruments([]); loadData();
    } catch { toast.error('Failed to bulk enable'); }
  };

  const handleOpenSettings = (symbol) => { setSelectedSymbol(symbol); setShowSettingsModal(true); };

  const filtered = () => {
    const data = activeTab === 'library' ? feedInstruments : enabledInstruments;
    return data.filter(inst => {
      const matchSearch = inst.symbol.toLowerCase().includes(searchQuery.toLowerCase());
      let matchSub = true;
      if (activeTab === 'library') {
        if (subscriptionFilter === 'subscribed') matchSub = inst.is_subscribed === 1;
        if (subscriptionFilter === 'not_subscribed') matchSub = inst.is_subscribed === 0;
      }
      return matchSearch && matchSub;
    });
  };

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    const items = filtered();
    return (
      <div className="flex flex-col -mx-4 -my-6 min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>

        {/* Hero */}
        <div className="px-4 pt-5 pb-5" style={{
          background: 'linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 60%, var(--accent-info)) 100%)'
        }}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] font-bold uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.6)' }}>Instruments</p>
            {wsConnected && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-full" style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)' }}>
                <Activity className="w-3 h-3" style={{ color: 'rgb(16, 185, 129)' }} />
                <span className="text-[10px] font-bold" style={{ color: 'rgb(16, 185, 129)' }}>LIVE</span>
              </div>
            )}
          </div>
          <p className="text-4xl font-black text-white leading-none font-mono">{items.length}</p>
          <p className="text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {activeTab === 'library' ? 'in library' : 'enabled'}
            {selectedInstruments.length > 0 && ` · ${selectedInstruments.length} selected`}
          </p>
        </div>

        {/* Tab bar */}
        <div className="flex border-b" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
          {[['library', 'Library'], ['enabled', 'Enabled']].map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className="flex-1 py-3 text-sm font-bold relative"
              style={{ color: activeTab === key ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>
              {label}
              {activeTab === key && <div className="absolute bottom-0 left-4 right-4 h-0.5 rounded-full" style={{ backgroundColor: 'var(--accent-primary)' }} />}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--text-tertiary)' }} />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search instruments…"
              className="w-full pl-9 pr-3 py-2 rounded-xl border text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-tertiary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>
        </div>

        {/* Category scroll */}
        <div className="flex gap-2 overflow-x-auto px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-color)' }}>
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setSelectedCategory(cat)}
              className="flex-shrink-0 text-[11px] font-bold px-3 py-1.5 rounded-full transition-colors"
              style={{
                backgroundColor: selectedCategory === cat ? 'var(--accent-primary)' : 'var(--bg-secondary)',
                color: selectedCategory === cat ? '#fff' : 'var(--text-secondary))',
                border: selectedCategory === cat ? 'none' : '1px solid var(--border-color)',
              }}>
              {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>

        {/* Sub-filter (library only) */}
        {activeTab === 'library' && (
          <div className="flex gap-2 px-4 py-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
            {[['all', 'All'], ['subscribed', 'Subscribed'], ['not_subscribed', 'Not Subscribed']].map(([key, label]) => (
              <button key={key} onClick={() => setSubscriptionFilter(key)}
                className="flex-shrink-0 text-[11px] font-bold px-3 py-1.5 rounded-full"
                style={{
                  backgroundColor: subscriptionFilter === key
                    ? key === 'subscribed' ? 'var(--accent-success)' : key === 'not_subscribed' ? 'var(--accent-info)' : 'var(--accent-primary)'
                    : 'var(--bg-secondary)',
                  color: subscriptionFilter === key ? '#fff' : 'var(--text-secondary)',
                  border: subscriptionFilter === key ? 'none' : '1px solid var(--border-color)',
                }}>
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Bulk enable bar */}
        {selectedInstruments.length > 0 && (
          <div className="px-4 py-2.5 border-b flex items-center justify-between"
            style={{ borderColor: 'var(--border-color)', backgroundColor: 'color-mix(in srgb, var(--accent-primary) 8%, transparent)' }}>
            <p className="text-xs font-bold" style={{ color: 'var(--accent-primary)' }}>
              {selectedInstruments.length} selected
            </p>
            <div className="flex gap-2">
              <button onClick={() => setSelectedInstruments([])}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg"
                style={{ color: 'var(--text-secondary)', border: '1px solid var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
                Clear
              </button>
              <button onClick={handleBulkEnable}
                className="text-xs font-bold px-3 py-1.5 rounded-lg text-white flex items-center gap-1"
                style={{ backgroundColor: 'var(--accent-primary)' }}>
                <Plus className="w-3 h-3" />Enable All
              </button>
            </div>
          </div>
        )}

        {/* Instrument list */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-primary)' }} />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No instruments found</p>
          </div>
        ) : (
          <div className="px-4 pt-3 pb-8 space-y-2">
            {items.map(instrument => (
              <MobileInstrumentRow
                key={instrument.symbol}
                instrument={instrument}
                price={prices.get(instrument.symbol)}
                isPricePending={wsConnected && pendingPrices.has(instrument.symbol)}
                isLibrary={activeTab === 'library'}
                isSelected={selectedInstruments.includes(instrument.symbol)}
                onSelect={sym => setSelectedInstruments(prev => prev.includes(sym) ? prev.filter(s => s !== sym) : [...prev, sym])}
                onEnable={handleEnableInstrument}
                onDisable={handleDisableInstrument}
                onSettings={handleOpenSettings}
              />
            ))}
          </div>
        )}

        {showSettingsModal && (
          <SymbolSettingsModal symbol={selectedSymbol}
            onClose={() => { setShowSettingsModal(false); setSelectedSymbol(null); }}
            onSave={async (settings) => {
              try { await updateSymbolSettings(selectedSymbol, settings); toast.success('Settings updated'); setShowSettingsModal(false); loadData(); }
              catch (e) { toast.error(e.message || 'Failed'); }
            }}
            fetchSymbolSettings={fetchSymbolSettings} />
        )}

        <div className="pb-[env(safe-area-inset-bottom)]" />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-2xl font-bold">Instruments</h1>
            {wsConnected && (
              <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
                <Activity className="w-3.5 h-3.5" style={{ color: 'rgb(16, 185, 129)' }} />
                <span className="text-xs font-bold" style={{ color: 'rgb(16, 185, 129)' }}>LIVE PRICES</span>
              </div>
            )}
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>Manage trading instruments and configure symbol settings</p>
        </div>
        {selectedInstruments.length > 0 && (
          <button onClick={handleBulkEnable}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-smooth"
            style={{ backgroundColor: 'var(--accent-primary)' }}>
            <Plus className="w-4 h-4" />Enable Selected ({selectedInstruments.length})
          </button>
        )}
      </div>

      <div className="flex gap-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
        {[['library', 'Instrument Library'], ['enabled', 'Enabled Instruments']].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className="px-4 py-2 font-medium transition-smooth relative"
            style={{ color: activeTab === key ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>
            {label}
            {activeTab === key && <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: 'var(--accent-primary)' }} />}
          </button>
        ))}
      </div>

      <div className="flex gap-4 flex-wrap">
        <div className="flex-1 min-w-[250px] relative">
          <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-tertiary)' }} />
          <input type="text" placeholder="Search instruments..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none transition-smooth"
            style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
        </div>
        {activeTab === 'library' && (
          <div className="flex gap-2">
            {[['all','All'], ['subscribed','Subscribed'], ['not_subscribed','Not Subscribed']].map(([key, label]) => (
              <button key={key} onClick={() => setSubscriptionFilter(key)}
                className="px-4 py-2 rounded-lg font-medium transition-smooth whitespace-nowrap"
                style={{
                  backgroundColor: subscriptionFilter === key
                    ? key === 'subscribed' ? 'var(--accent-success)' : key === 'not_subscribed' ? 'var(--accent-info)' : 'var(--accent-primary)'
                    : 'var(--bg-secondary)',
                  color: subscriptionFilter === key ? '#fff' : 'var(--text-secondary)',
                }}>
                {label}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setSelectedCategory(cat)}
              className="px-4 py-2 rounded-lg font-medium transition-smooth whitespace-nowrap"
              style={{
                backgroundColor: selectedCategory === cat ? 'var(--accent-primary)' : 'var(--bg-secondary)',
                color: selectedCategory === cat ? '#fff' : 'var(--text-secondary)',
              }}>
              {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-primary)' }} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered().map(instrument => (
            <InstrumentCard key={instrument.symbol} instrument={instrument} 
              price={prices.get(instrument.symbol)}
              isPricePending={wsConnected && pendingPrices.has(instrument.symbol)}
              isLibrary={activeTab === 'library'}
              isSelected={selectedInstruments.includes(instrument.symbol)}
              onSelect={sym => setSelectedInstruments(prev => prev.includes(sym) ? prev.filter(s => s !== sym) : [...prev, sym])}
              onEnable={handleEnableInstrument} onDisable={handleDisableInstrument} onSettings={handleOpenSettings} />
          ))}
        </div>
      )}

      {filtered().length === 0 && !loading && (
        <div className="text-center py-12" style={{ color: 'var(--text-secondary)' }}>No instruments found</div>
      )}

      {showSettingsModal && (
        <SymbolSettingsModal symbol={selectedSymbol}
          onClose={() => { setShowSettingsModal(false); setSelectedSymbol(null); }}
          onSave={async (settings) => {
            try { await updateSymbolSettings(selectedSymbol, settings); toast.success('Settings updated'); setShowSettingsModal(false); loadData(); }
            catch (e) { toast.error(e.message || 'Failed'); }
          }}
          fetchSymbolSettings={fetchSymbolSettings} />
      )}
    </div>
  );
}

// ─── Mobile instrument row ────────────────────────────────────────────────────
function MobileInstrumentRow({ instrument, price, isPricePending, isLibrary, isSelected, onSelect, onEnable, onDisable, onSettings }) {
  const color = getCategoryColor(instrument.category);
  const hasPrice = price && price.bid && price.ask;

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-2xl border"
      style={{
        backgroundColor: isSelected ? 'color-mix(in srgb, var(--accent-primary) 8%, transparent)' : 'var(--bg-secondary)',
        borderColor: isSelected ? 'var(--accent-primary)' : 'var(--border-color)',
      }}>
      {/* Category dot + symbol */}
      <div className="flex-1 min-w-0 flex items-center gap-2.5">
        {isLibrary && (
          <input type="checkbox" checked={isSelected} onChange={() => onSelect(instrument.symbol)}
            disabled={instrument.is_subscribed}
            className="w-4 h-4 flex-shrink-0 rounded disabled:opacity-40" />
        )}
        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <p className="text-sm font-black" style={{ color: 'var(--text-primary)' }}>{instrument.symbol}</p>
            {instrument.is_subscribed && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-success) 15%, transparent)', color: 'var(--accent-success)' }}>
                ON
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <p className="text-[10px] capitalize" style={{ color }}>{instrument.category}</p>
            {/* Price area: skeleton → real price */}
            {isPricePending && !hasPrice ? (
              <PriceSkeleton compact />
            ) : hasPrice ? (
              <div className="flex items-center gap-1.5">
                <span className="text-[9px] font-mono" style={{ color: 'var(--accent-danger)' }}>{price.bid.toFixed(instrument.price_scale)}</span>
                <span className="text-[9px]" style={{ color: 'var(--text-tertiary)' }}>/</span>
                <span className="text-[9px] font-mono" style={{ color: 'var(--accent-success)' }}>{price.ask.toFixed(instrument.price_scale)}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Action */}
      {isLibrary ? (
        <button onClick={() => onEnable(instrument.symbol)} disabled={instrument.is_subscribed}
          className="flex-shrink-0 text-[11px] font-bold px-3 py-1.5 rounded-xl flex items-center gap-1 disabled:opacity-40"
          style={{
            backgroundColor: instrument.is_subscribed ? 'var(--bg-tertiary)' : 'color-mix(in srgb, var(--accent-success) 15%, transparent)',
            color: instrument.is_subscribed ? 'var(--text-tertiary)' : 'var(--accent-success)',
          }}>
          <Check className="w-3 h-3" />{instrument.is_subscribed ? 'On' : 'Enable'}
        </button>
      ) : (
        <div className="flex gap-1.5 flex-shrink-0">
          <button onClick={() => onSettings(instrument.symbol)}
            className="text-[11px] font-bold px-2.5 py-1.5 rounded-xl flex items-center gap-1"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent-primary) 15%, transparent)', color: 'var(--accent-primary)' }}>
            <Settings className="w-3 h-3" />
          </button>
          <button onClick={() => onDisable(instrument.symbol)}
            className="text-[11px] font-bold px-2.5 py-1.5 rounded-xl"
            style={{ backgroundColor: 'color-mix(in srgb, var(--accent-danger) 15%, transparent)', color: 'var(--accent-danger)' }}>
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Desktop instrument card ──────────────────────────────────────────────────
function InstrumentCard({ instrument, price, isPricePending, isLibrary, isSelected, onSelect, onEnable, onDisable, onSettings }) {
  const color = getCategoryColor(instrument.category);
  const hasPrice = price && price.bid && price.ask;
  const spread = hasPrice ? ((price.ask - price.bid) * Math.pow(10, instrument.price_scale)).toFixed(1) : null;

  return (
    <div className="p-4 rounded-lg border transition-smooth"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderColor: isSelected ? 'var(--accent-primary)' : 'var(--border-color)',
        backgroundImage: isSelected ? 'linear-gradient(color-mix(in srgb, var(--accent-primary) 5%, transparent), color-mix(in srgb, var(--accent-primary) 5%, transparent))' : 'none',
      }}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-bold text-lg">{instrument.symbol}</h3>
            {instrument.is_subscribed && (
              <span className="px-2 py-0.5 rounded text-xs font-medium border"
                style={{ backgroundColor: 'color-mix(in srgb, var(--accent-success) 20%, transparent)', color: 'var(--accent-success)', borderColor: 'color-mix(in srgb, var(--accent-success) 30%, transparent)' }}>
                Subscribed
              </span>
            )}
          </div>
          <span className="inline-block px-2 py-1 rounded text-xs font-medium"
            style={{ backgroundColor: `color-mix(in srgb, ${color} 20%, transparent)`, color }}>
            {instrument.category}
          </span>
        </div>
        {isLibrary && (
          <input type="checkbox" checked={isSelected} onChange={() => onSelect(instrument.symbol)}
            disabled={instrument.is_subscribed}
            className="w-5 h-5 rounded border-2 disabled:opacity-50 disabled:cursor-not-allowed" />
        )}
      </div>

      {/* Live Prices — skeleton while waiting, real data once received */}
      {isPricePending && !hasPrice ? (
        <PriceSkeleton />
      ) : hasPrice ? (
        <div className="mb-3 p-3 rounded-lg" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1">
              <TrendingDown className="w-3 h-3" style={{ color: 'var(--accent-danger)' }} />
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Bid:</span>
            </div>
            <span className="font-mono font-bold" style={{ color: 'var(--accent-danger)' }}>
              {price.bid.toFixed(instrument.price_scale)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3" style={{ color: 'var(--accent-success)' }} />
              <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Ask:</span>
            </div>
            <span className="font-mono font-bold" style={{ color: 'var(--accent-success)' }}>
              {price.ask.toFixed(instrument.price_scale)}
            </span>
          </div>
          {spread && (
            <div className="mt-2 pt-2 border-t flex items-center justify-between text-xs"
              style={{ borderColor: 'var(--border-color)' }}>
              <span style={{ color: 'var(--text-secondary)' }}>Spread:</span>
              <span className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{spread} pips</span>
            </div>
          )}
        </div>
      ) : null}

      <div className="space-y-2 mb-4 text-sm">
        <div className="flex justify-between">
          <span style={{ color: 'var(--text-secondary)' }}>Price Scale:</span>
          <span className="font-mono">{instrument.price_scale}</span>
        </div>
        {!isLibrary && instrument.spread_markup !== undefined && (
          <div className="flex justify-between">
            <span style={{ color: 'var(--text-secondary)' }}>Spread Markup:</span>
            <span className="font-mono">{instrument.spread_markup} pips</span>
          </div>
        )}
      </div>
      <div className="flex gap-2">
        {isLibrary ? (
          <button onClick={() => onEnable(instrument.symbol)} disabled={instrument.is_subscribed}
            className="flex-1 py-2 rounded-lg transition-smooth text-sm font-medium"
            style={{
              backgroundColor: instrument.is_subscribed ? 'var(--bg-tertiary)' : 'var(--accent-success)',
              color: instrument.is_subscribed ? 'var(--text-secondary)' : '#fff',
              cursor: instrument.is_subscribed ? 'not-allowed' : 'pointer',
            }}>
            <Check className="w-4 h-4 inline mr-1" />{instrument.is_subscribed ? 'Enabled' : 'Enable'}
          </button>
        ) : (
          <>
            <button onClick={() => onSettings(instrument.symbol)}
              className="flex-1 py-2 text-white rounded-lg transition-smooth text-sm font-medium"
              style={{ backgroundColor: 'var(--accent-primary)' }}>
              <Settings className="w-4 h-4 inline mr-1" />Settings
            </button>
            <button onClick={() => onDisable(instrument.symbol)}
              className="px-3 py-2 text-white rounded-lg transition-smooth"
              style={{ backgroundColor: 'var(--accent-danger)' }}>
              <X className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}